import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ErrorService } from '../services/error.service';
import { forkJoin, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { LocalAuthService } from '../auth/local-auth.service';
import { ConfigService } from '../config.service';
import { ApiKey, ApiKeyService } from './api-key.service';
import { DownloadService, DownloadResult } from '../utility/download.service';
import { LatLng, ShirtDetails, MessageServiceEnum, BackgroundCheckTypes} from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { McDataMapService } from './mc-data-map.service';
import { BasicParticipantDetails, BasicParticipantDetail, ParticipantRoles, Lodging, Newuser, SimpleNewuser, NewtripUser } from './types/participants';
import { FilterExcludingWhere } from '@loopback/filter';

export interface ParticipantSearchParams {
  newuserIds?: number[];
}

export interface ParticipantSearchResult {
  id: string,
  firstName: string,
  lastName: string,
  email: string
}
export interface ParticipantUpdatedArgs {
  tripId: number,
  participantId: number
  operation: ParticipantUpdateOps
}

export type ParticipantUpdateOps = "update" | "add" | "remove"

export interface ImpersonationTab {
  closed: boolean;
  close(): void;
  location: { href: string };
}

export interface ImpersonationWindow {
  open(url: string, target: string): ImpersonationTab | null;
}

export const IMPERSONATION_WINDOW = new InjectionToken<ImpersonationWindow>(
  'IMPERSONATION_WINDOW',
  { providedIn: 'root', factory: (): ImpersonationWindow => window },
);

type SeasonalRecommendationBody = {
  newuserId: number;
  tripId: number;
  notes: string;
}

type PromoteGlBody = {
  newGlId: number,
  tripId: number,
  removeOldGl: boolean
}

export type PromoteGlResult = {
  oldGl: NewtripUser | undefined,
  newGl: NewtripUser
}

type RemoveParticipantBody = {
  newuserId: number,
  tripId: number,
  removeRosterSpace: boolean
}

type AddParticipantBody = {
  tripId: number,
  participants: Array<Pick<BasicParticipantDetail, "firstname" | "lastname" | "email" | "rolelead" | "rolefin" | "roledrive"> & { permissionsOnly: boolean }>

}

export interface RemainingParticipantsResponse {
  remainingParticipants: RemainingParticipantsStats,
  totalParticipants: RemainingParticipantsStats
}

export interface RemainingParticipantsStats {
  attendees: number,
  tripCount: number
}



// export interface NewuserResolverParams {
//   newuserIds?: number[],
//   tripIds?: number[],
//   smsActive?: boolean,
//   radiusSearch?: { radiusCenter?: LatLng, radiusDistance?: number },
//   minAge?: number,

// }

@Injectable({
  providedIn: 'root'
})
export class ParticipantService {

  private readonly errorService = inject(ErrorService);
  private readonly messageService = inject(MessageService);
  private readonly impersonationWindow = inject(IMPERSONATION_WINDOW);

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private mcDataMapService: McDataMapService,
    private apiKeyService: ApiKeyService,
    private auth: LocalAuthService,
    private downloadService: DownloadService
  ) {
    this.apiKeyService.getLegacyMcStaffKey().subscribe(apiKey => this.apiKey = apiKey)
  }

  readonly basicParticipantDateFields: DateField<BasicParticipantDetail>[] = [{ dateType: "utc", fieldName: "legalTimestamp" }, { dateType: "utc", fieldName: "riskTimestamp" }]

  readonly participantUpdated: Subject<ParticipantUpdatedArgs> = new Subject<ParticipantUpdatedArgs>()

  private apiKey!: ApiKey

  getParticipantByEmail(email: string): Observable<SimpleNewuser> {

    const filter: FilterExcludingWhere<Newuser> = {
      fields: {
        id: true,
        firstname: true,
        lastname: true,
        email: true
      }
    }

  
    const params = new HttpParams().set("filter", JSON.stringify(filter)).set("email", email)

    return this.http.get<SimpleNewuser>(`${this.config.dataApiUrl}/participants`, {params: params})
  }

  getShirtDetails(tripId: number) {
    return this.http.get<ShirtDetails[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/participants/shirt-details`)
  }

  getBasicParticipantDetails(tripId: number): Observable<BasicParticipantDetails> {
    return this.http.get<BasicParticipantDetails>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/participants/basic`).pipe(
      switchMap(details => {

        return forkJoin([
          DataTransform.replaceDatesArray<BasicParticipantDetail>(details.nonRosterUsers, this.basicParticipantDateFields),
          DataTransform.replaceDatesArray<BasicParticipantDetail>(details.rosterParticipants, this.basicParticipantDateFields)
        ])
          .pipe(
            switchMap(results => {
              return of({
                rosterParticipants: results[1],
                nonRosterUsers: results[0]
              })
            })
          )
      })
    )
  }

  getBasicRosterDetails(tripId: number): Observable<BasicParticipantDetail[]> {
    return this.http.get<BasicParticipantDetail[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/participants/roster/basic`).pipe(
      switchMap(roster => {
        return DataTransform.replaceDatesArray<BasicParticipantDetail>(roster, this.basicParticipantDateFields)
      })
    )
  }

  getBasicPermissionOnlyDetails(tripId: number): Observable<BasicParticipantDetail[]> {
    return this.http.get<BasicParticipantDetail[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/participants/permission-only/basic`).pipe(
      switchMap(roster => {
        return DataTransform.replaceDatesArray<BasicParticipantDetail>(roster, this.basicParticipantDateFields)
      })
    )
  }

  resendTripInvitation(tripId: number, newuserIds: number[]) {
    return this.http.post(`${this.config.dataApiUrl}/participants/resend-invitation`, { tripId: tripId, userIds: newuserIds })
  }

  sendPasswordResetEmail(tripId: number, newuserId: number) {
    return this.http.post(`${this.config.dataApiUrl}/participants/reset-password`, { tripId: tripId, userId: newuserId })
  }

  updateEmailAddress(tripId: number, newuserId: number, newEmail: string) {
    return this.http.post(`${this.config.dataApiUrl}/participants/update-email`, { tripId: tripId, userId: newuserId, email: newEmail }).pipe(
      tap(result => {
        this.participantUpdated.next({ tripId: tripId, participantId: newuserId, operation: "update" })
      })
    )
  }

  updateRoles(tripId: number, newuserId: number, roles: ParticipantRoles) {
    return this.http.put(`${this.config.dataApiUrl}/participants/permissions/`, { tripId: tripId, userId: newuserId, ...roles }).pipe(
      tap(result => {
        this.participantUpdated.next({ tripId: tripId, participantId: newuserId, operation: "update" })
      })
    )
  }

  updateBackgroundCheck(tripId: number, newuserId: number, backgroundCheckType: BackgroundCheckTypes) {
    return this.http.put(`${this.config.dataApiUrl}/participants/update-background-check`, {tripId: tripId, userId: newuserId, backgroundCheckType: backgroundCheckType}).pipe(
      tap(result => {
        this.participantUpdated.next({tripId: tripId, participantId: newuserId, operation: "update"})
      })
    )
  }

  openParticipantAccount(newuserId: number, tripId: number): void {
    const tab: ImpersonationTab | null = this.impersonationWindow.open(
      'about:blank',
      '_blank',
    );
    if (!tab) {
      this.errorService.error(
        'Unable to open this account.',
        new Error('The browser blocked the new tab.'),
        this.messageService,
      );
      return;
    }

    this.http
      .post<void>(
        `${this.config.pelicanApiUrl}/users/${newuserId.toString()}/staff-impersonation`,
        null,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${this.auth.getAuthToken()}`,
          },
        },
      )
      .subscribe({
        next: () => {
          if (!tab.closed) {
            const accountUrl = new URL(this.config.pelicanAccountUrl);
            accountUrl.searchParams.set('tripId', tripId.toString());
            tab.location.href = accountUrl.toString();
          }
        },
        error: (err: unknown) => {
          tab.close();
          this.errorService.error(
            'Unable to open this account.',
            err,
            this.messageService,
          );
        },
      });
  }

  recommendSeasonalStaff(args: SeasonalRecommendationBody) {
    return this.http.post(`${this.config.dataApiUrl}/participants/seasonal-recommendation`, args)
  }

  promoteGl(args: PromoteGlBody) {
    return this.http.post<PromoteGlResult>(`${this.config.dataApiUrl}/participants/promote-gl`, args).pipe(
      tap(result => {
        if (result.oldGl) {
        this.participantUpdated.next({ tripId: result.oldGl.tripId, participantId: result.oldGl.newuserId, operation: args.removeOldGl ? "remove" : "update" })
        }
        this.participantUpdated.next({ tripId: result.newGl.tripId, participantId: result.newGl.newuserId, operation: "update" })
      })
    )
  }

  removeParticipant(args: RemoveParticipantBody) {
    return this.http.post(`${this.config.dataApiUrl}/participants/remove-participant`, args).pipe(
      tap(result => {
        this.participantUpdated.next({ tripId: args.tripId, participantId: args.newuserId, operation: "remove" })
      })
    )
  }

  addParticipant(args: AddParticipantBody) {
    return this.http.post(`${this.config.dataApiUrl}/participants`, args).pipe(
      tap(result => {
        this.participantUpdated.next({ tripId: args.tripId, participantId: 0, operation: "add" })
      })
    )
  }

  getLodging(tripId: number) {
    return this.http.get<Lodging[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/lodging`).pipe(
      switchMap(lodging => {
        return DataTransform.replaceDatesArray<Lodging>(lodging, [{ dateType: "local", fieldName: "startDate" }, { dateType: "local", fieldName: "endDate" }])
      }),
      switchMap(lodging => {
        return DataTransform.replaceJsonArray<Lodging>(lodging, ["roomList"])
      })
    )
  }


  downloadRosterPdf(tripId: number): Observable<DownloadResult> {
    return this.downloadService.downloadFile(
      `${this.config.dataApiUrl}/trips/${tripId.toString()}/roster/pdf`,
      'roster.pdf'
    );
  } 

 

  downloadFormsPdf(tripId: number): Observable<DownloadResult> {
    return this.downloadService.downloadFile(
      `${this.config.dataApiUrl}/trips/${tripId.toString()}/forms/pdf`,
      'forms.pdf'
    );
  }


  participantSearch(params: ParticipantSearchParams = {}): Observable<ParticipantSearchResult[]> {
    let httpParams = new HttpParams();
    if (params.newuserIds?.length) {
      for (const id of params.newuserIds) {
        httpParams = httpParams.append('newuserIds', id.toString());
      }
    }

    return this.http
      .get<Array<Omit<ParticipantSearchResult, 'id'> & { id: number }>>(
        `${this.config.dataApiUrl}/text-messaging/audience`,
        { params: httpParams },
      )
      .pipe(map((rows) => rows.map((row) => ({ ...row, id: String(row.id) }))));
  }

  sendTextMessage(newuserIds: number[],message: {message: string, messageService: MessageServiceEnum} ) {

    let postArgs = {
      messageText: message.message,
     messageService: message.messageService,
      newuserIds: newuserIds
    }

   return this.http.post(`${this.config.dataApiUrl}/text-messaging`, {...postArgs})
  }

  getRemainingParticipants() {
    return this.http.get<RemainingParticipantsResponse>(`${this.config.dataApiUrl}/participants/remaining`)
  }

  emptyLodging(): Lodging {
    return {
      address1: "",
      address2: "",
      city: "",
      country: "",
      description: "",
      email: "",
      endDate: new Date(1000, 0, 1),
      id: 0,
      name: "",
      phone: "",
      roomList: [],
      startDate: new Date(1000, 0, 1),
      state: "",
      tripId: 0,
      website: "",
      zipcode: ""
    }
  }

}
